import {
  type CacheStorage,
  type ExperimentCacheEntry,
  getPageKey,
  loadCacheEntry,
  removeCacheEntry,
  saveCacheEntry,
} from './experimentCache';
import { sanitizeHtml } from './sanitizeHtml';

interface AnalyticsEvent {
  event: string;
  timestamp: number;
  url: string;
  userAgent: string;
  visitorId: string;
  data?: unknown;
}

/**
 * /variant API のレスポンス。実験が無いページでは各値が null になる。
 */
interface VariantResponse {
  experimentId?: string | null;
  caseId?: string | null;
  pattern?: string | null;
  xPath?: string | null;
  customHtml?: string | null;
}

const EVENT_NAMES = {
  CLICK_EVENT: 'click',
  PV_EVENT: 'page_view',
};

// 環境変数（ビルド時に置き換えられる）
const API_BASE_URL =
  process.env.API_BASE_URL ||
  'https://atzby1t570.execute-api.ap-northeast-1.amazonaws.com/stg';

const ANTI_FLICKER_ID = 'itera-antiflicker';
// 旧バージョンとの互換性のため、古いIDも保持
const ANTI_FLICKER_ID_LEGACY = 'agile-base-antiflicker';
// アンチフリッカーを強制的に解除するまでの時間（API が応答しない場合でもページを表示するための保険）
const ANTI_FLICKER_TIMEOUT_MS = 3000;
// /variant API のタイムアウト。超えた場合は元のページを表示する
const VARIANT_TIMEOUT_MS = 3000;
// 詳細ログは ITERA_DEBUG=true を明示してビルドした場合のみ出力する（顧客サイトに配信するバンドルでは常に無効）
const DEBUG = process.env.ITERA_DEBUG === 'true';

const debugLog = (...args: unknown[]): void => {
  if (DEBUG) console.log(...args);
};

const debugWarn = (...args: unknown[]): void => {
  if (DEBUG) console.warn(...args);
};

class Analytics {
  // 生成するID
  private visitorId: string;
  private serviceKey: string | undefined = undefined;
  // ABテスト用のcaseIdとpattern
  private currentCaseId: string | undefined = undefined;
  private currentPattern: string | undefined = undefined;
  private currentExperimentId: string | undefined = undefined;
  // scriptタグの参照（data属性取得用）
  private scriptElement: HTMLScriptElement | null = null;

  constructor() {
    // scriptタグの参照を取得（document.currentScriptはコンストラクタ実行中のみ有効）
    this.scriptElement = document.currentScript as HTMLScriptElement;

    this.visitorId = this.generateVisitorId();
    this.serviceKey = this.getServiceKey();
    this.setCookie('visitorId', this.visitorId, 365);
    // Cookieから実験情報を復元
    this.restoreExperimentFromCookie();
    // アンチフリッカースタイルを即座に適用
    this.injectAntiFlickerStyle();
    this.init();

    // XPath Pickerの初期化（data-enable-xpath-picker="true"の場合のみ）
    if (
      this.scriptElement &&
      this.scriptElement.dataset.enableXpathPicker === 'true'
    ) {
      this.initXPathPicker();
    }
  }

  /**
   * Cookieから実験情報（caseId, pattern, experimentId）を復元
   */
  private restoreExperimentFromCookie(): void {
    const experimentData = this.getCookie('agilebase_experiment');
    if (experimentData) {
      try {
        const data = JSON.parse(experimentData);
        this.currentCaseId = data.caseId;
        this.currentPattern = data.pattern;
        this.currentExperimentId = data.experimentId;
        debugLog(`[AgileBase] Restored experiment from cookie:`, data);
      } catch (e) {
        console.warn('[AgileBase] Failed to parse experiment cookie:', e);
      }
    }
  }

  /**
   * 実験情報をCookieに保存
   */
  private saveExperimentToCookie(
    caseId: string,
    pattern: string,
    experimentId: string,
  ): void {
    const data = JSON.stringify({ caseId, pattern, experimentId });
    // 30日間保存
    this.setCookie('agilebase_experiment', data, 30);
    debugLog(`[AgileBase] Saved experiment to cookie:`, {
      caseId,
      pattern,
      experimentId,
    });
  }

  /**
   * Cookie の実験情報が指定した実験のものであれば削除する（終了した実験の割り当てを残さないため）
   */
  private clearExperimentCookie(experimentId: string): void {
    const raw = this.getCookie('agilebase_experiment');
    if (!raw) return;
    try {
      const data: unknown = JSON.parse(raw);
      if (
        typeof data === 'object' &&
        data !== null &&
        (data as { experimentId?: unknown }).experimentId === experimentId
      ) {
        this.setCookie('agilebase_experiment', '', -1);
        debugLog(
          '[AgileBase] Cleared ended experiment from cookie:',
          experimentId,
        );
      }
    } catch {
      // 壊れたCookieは削除する
      this.setCookie('agilebase_experiment', '', -1);
    }
  }

  /**
   * LocalStorage を取得する。Safari のプライベートモードやサイト設定でブロックされている場合は null
   */
  private getStorage(): CacheStorage | null {
    try {
      return window.localStorage;
    } catch {
      return null;
    }
  }

  /**
   * 現在のページの実験キャッシュを読み込む。
   * 保存時と visitorId が異なる場合（visitorId の Cookie だけが ITP や手動削除で消えた場合など）は、
   * Lambda が新しい visitorId に別のパターンを割り当てる可能性があり、表示と割り当てが食い違うため破棄する
   */
  private loadExperimentCache(pageKey: string): ExperimentCacheEntry | null {
    const storage = this.getStorage();
    if (!storage || !this.serviceKey) return null;
    const cache = loadCacheEntry(storage, this.serviceKey, pageKey);
    if (cache && cache.visitorId !== this.visitorId) {
      debugLog('[AgileBase] Cache visitorId mismatch, discarded.');
      this.clearExperimentCache(pageKey);
      return null;
    }
    return cache;
  }

  /**
   * APIレスポンスに合わせて現在のページの実験キャッシュを更新する。
   * バリアント（xPath・customHtmlあり）の場合のみ保存し、それ以外（コントロール群・実験なし）は削除する
   */
  private syncExperimentCache(pageKey: string, data: VariantResponse): void {
    const storage = this.getStorage();
    if (!storage || !this.serviceKey) return;
    if (
      data.experimentId &&
      data.caseId &&
      data.pattern &&
      data.xPath &&
      data.customHtml
    ) {
      saveCacheEntry(storage, this.serviceKey, pageKey, {
        visitorId: this.visitorId,
        experimentId: data.experimentId,
        caseId: data.caseId,
        pattern: data.pattern,
        xPath: data.xPath,
        customHtml: data.customHtml,
      });
      debugLog('[AgileBase] Saved experiment to cache:', pageKey);
    } else {
      this.clearExperimentCache(pageKey);
    }
  }

  /**
   * 現在のページの実験キャッシュを削除
   */
  private clearExperimentCache(pageKey: string): void {
    const storage = this.getStorage();
    if (!storage || !this.serviceKey) return;
    removeCacheEntry(storage, this.serviceKey, pageKey);
    debugLog('[AgileBase] Experiment cache cleared:', pageKey);
  }

  private getServiceKey(): string {
    // パターン1: scriptタグのdata-service-key属性から取得（推奨）
    if (this.scriptElement && this.scriptElement.dataset.serviceKey) {
      return this.scriptElement.dataset.serviceKey;
    }

    // パターン2: グローバル変数から取得（後方互換性のため）
    if (
      typeof window !== 'undefined' &&
      (window as unknown as { AGILE_BASE_SERVICE_KEY?: string })
        .AGILE_BASE_SERVICE_KEY
    ) {
      return (window as unknown as { AGILE_BASE_SERVICE_KEY: string })
        .AGILE_BASE_SERVICE_KEY;
    }

    // デフォルト値またはエラー
    console.error(
      '[AgileBase] Service key not found. Please set data-service-key attribute or AGILE_BASE_SERVICE_KEY.',
    );
    return '';
  }

  private generateVisitorId(): string {
    const existingId = this.getCookie('visitorId');
    if (existingId) {
      return existingId;
    }

    // より一様な乱数を生成（ABテスト用）
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return 'visitor_' + crypto.randomUUID();
    }

    // フォールバック: crypto.getRandomValues
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      const array = new Uint32Array(4);
      crypto.getRandomValues(array);
      return 'visitor_' + Array.from(array, (x) => x.toString(36)).join('');
    }

    // 最終フォールバック（非推奨だが互換性のため）
    return 'visitor_' + Math.random().toString(36).substr(2, 9);
  }

  private init(): void {
    debugLog(`[AgileBase] Initialized - Visitor ID: ${this.visitorId}`);
    debugLog(`[AgileBase] Service Key: ${this.serviceKey || 'Not set'}`);
    debugLog(`[AgileBase] Page loaded: ${window.location.href}`);

    // DOMが読み込まれたらABテスト反映（PVログはmodifyDOM完了後に送信）
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        debugLog('dom loaded');
        this.modifyDOM();
      });
    } else {
      this.modifyDOM();
    }

    // イベントリスナーの設置
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    // すべての要素のクリックイベントを送信
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      this.track(EVENT_NAMES.CLICK_EVENT, {
        element: target.tagName.toLowerCase(),
        className: target.className || 'none',
      });
    });
  }

  /**
   * ログの送信
   * @param experiment - イベントを紐付ける実験。省略時はこのページの割り当て（currentCaseId / currentPattern）を使う
   */
  public track(
    eventName: string,
    data?: Record<string, unknown>,
    experiment?: { caseId: string; pattern: string },
  ): void {
    const event: AnalyticsEvent = {
      event: eventName,
      timestamp: Date.now(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      visitorId: this.visitorId,
      data: {
        ...data,
      },
    };

    this.sendEvent(
      event,
      this.serviceKey,
      experiment?.caseId ?? this.currentCaseId,
      experiment?.pattern ?? this.currentPattern,
    );
  }

  /**
   * 外部APIにアナリティクスイベントを送信します
   *
   * @param event - 送信するイベントデータ (イベント名、タイムスタンプ、URL、ユーザーエージェント、訪問者ID、データを含む)
   * @param serviceKey - サービス認証キー。未定義の場合は送信をスキップします
   * @param caseId - ABテストのケースID (オプション)
   * @param pattern - ABテストのパターン情報 (オプション)
   * @returns Promise<void> - 送信完了時に解決されるPromise
   *
   * @example
   * ```typescript
   * const event = {
   *   event: 'page_view',
   *   timestamp: Date.now(),
   *   url: window.location.href,
   *   userAgent: navigator.userAgent,
   *   visitorId: 'visitor_123',
   *   data: { page: 'home' }
   * };
   * await analytics.sendEvent(event, 'service_key_123', 'case_456', 'pattern_A');
   * ```
   *
   * @throws エラーが発生した場合はコンソールにログを出力しますが、例外は投げません
   */
  public async sendEvent(
    event: AnalyticsEvent,
    serviceKey: string | undefined,
    caseId: string | undefined,
    pattern: string | undefined,
  ): Promise<void> {
    const eventPayload = {
      caseId: caseId,
      pattern: pattern,
      event: event.event,
      timestamp: event.timestamp,
      url: event.url,
      userAgent: event.userAgent,
      visitorId: event.visitorId,
      data: event.data,
    };

    debugLog(`[AgileBase] Event tracked:`, eventPayload);

    if (serviceKey === undefined) {
      console.warn('[AgileBase] serviceKey is not found.');
      return;
    }

    try {
      // 外部APIにイベントデータを送信
      const response = await fetch(`${API_BASE_URL}/logs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'agile-base-service-key': `${serviceKey}`,
        },
        body: JSON.stringify(eventPayload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      debugLog(`[AgileBase] Event sent successfully.`);
    } catch (error) {
      console.error(`[AgileBase] Failed to send event:`, error);
    }
  }

  public getVisitorId(): string {
    return this.visitorId;
  }

  private setCookie(name: string, value: string, days: number): void {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  }

  private getCookie(name: string): string | null {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      if (c) {
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0)
          return c.substring(nameEQ.length, c.length);
      }
    }
    return null;
  }

  // private hashString(str: string): number {
  //   let hash = 0;
  //   for (let i = 0; i < str.length; i++) {
  //     const char = str.charCodeAt(i);
  //     hash = (hash << 5) - hash + char;
  //     hash = hash & hash;
  //   }
  //   return Math.abs(hash);
  // }

  /**
   * バリアントの表示と割り当ての保存を行う。キャッシュの有無と API の結果の組み合わせごとの動作は次のとおり
   * （modifyDOM.test.ts の各テストと対応）。
   *
   * | キャッシュ        | API の結果                    | このページの表示 | このページのイベント | Cookie                         | キャッシュ                       |
   * |-------------------|-------------------------------|------------------|----------------------|--------------------------------|----------------------------------|
   * | 適用成功          | 同じ実験・同じパターン        | キャッシュ       | キャッシュのパターン | キャッシュのパターンで保存     | 更新                             |
   * | 適用成功          | 同じ実験・別パターン（比率変更）| キャッシュ     | キャッシュのパターン | キャッシュのパターンで保存     | 新パターンで更新（次の PV で切替）|
   * | 適用成功          | 同じ実験・コントロール群      | キャッシュ       | キャッシュのパターン | キャッシュのパターンで保存     | 削除（次の PV は元のページ）     |
   * | 適用成功          | 実験なし（終了）              | キャッシュ       | キャッシュのパターン | その実験のものなら削除         | 削除                             |
   * | 適用成功          | 別の実験                      | キャッシュ       | キャッシュのパターン | その実験のものなら削除         | 新しい実験で更新                 |
   * | 適用成功          | API エラー・タイムアウト      | キャッシュ       | キャッシュのパターン | キャッシュのパターンで保存     | 変更なし                         |
   * | なし / 適用失敗   | 実験あり・バリアント          | API の結果       | API のパターン       | API のパターンで保存           | 保存                             |
   * | なし / 適用失敗   | 実験あり・コントロール群      | 元のページ       | API のパターン       | API のパターンで保存           | 削除                             |
   * | なし / 適用失敗   | 実験なし（ゴールページ等）    | 元のページ       | Cookie から復元      | 変更なし                       | 削除                             |
   * | なし / 適用失敗   | API エラー・タイムアウト      | 元のページ       | Cookie から復元      | 変更なし                       | 変更なし（適用失敗時は削除済み） |
   *
   * 「このページのイベント」は実際に表示したパターンに紐付け、Cookie は API で実験の継続を確認してから保存する。
   */
  private async modifyDOM(): Promise<void> {
    // このページでキャッシュから適用した実験（未適用なら null）
    let appliedCache: ExperimentCacheEntry | null = null;
    try {
      if (!this.serviceKey) {
        console.warn(
          '[AgileBase] Service key not found, skipping A/B test modifications',
        );
        // serviceKeyがなくてもPVログは送信（Cookieから復元した情報があれば使用）
        this.track(EVENT_NAMES.PV_EVENT);
        // アンチフリッカースタイルを削除
        this.removeAntiFlickerStyle();
        return;
      }

      // stale-while-revalidate:
      // このページのキャッシュがあれば即時にDOMを書き換えてフリッカーを防ぎ、
      // その後も必ずAPIで再検証してキャッシュ・Cookieを最新化する（実験の停止・編集・比率変更は次のPVで反映）
      const pageKey = getPageKey(window.location.href);
      const cache = this.loadExperimentCache(pageKey);
      if (cache) {
        appliedCache = this.applyVariant(cache) ? cache : null;
        if (appliedCache) {
          debugLog('[AgileBase] Cache hit, applied DOM from cache.');
          // 訪問者が実際に見ているのはキャッシュのパターンなので、このページのイベントはそれに紐付ける。
          // Cookie は実験が継続中かをAPIで確認してから保存する（停止済みの実験をCookieに残さないため）
          this.setCurrentExperiment(appliedCache);
        } else {
          // 要素が見つからない等 → キャッシュが陳腐化しているため破棄
          this.clearExperimentCache(pageKey);
        }
      }

      // 外部APIにバリアント情報を要求
      const request = {
        visitorId: this.visitorId,
        url: window.location.href,
      };
      /**
       * レスポンスイメージ
       * {
            'id': '',
            'caseId': ''
            'pattern': pattern,
            'xPath': '/html/body/div/div/header/div[1]/div/div/a',
            'customHtml': '<div>AgileBae!!</div>'
        }
       */
      const responseData = await this.fetchVariant(request);
      debugLog('[AgileBase] Received variant response', responseData);

      // キャッシュを再検証結果で更新（実験終了・コントロール群の場合は削除）
      this.syncExperimentCache(pageKey, responseData);

      if (appliedCache) {
        // キャッシュで適用済みの場合、このページの表示とイベントの紐付けはキャッシュのパターンのまま維持する。
        if (responseData.experimentId === appliedCache.experimentId) {
          // 実験は継続中：Cookie も実際に表示したパターンで保存する。
          // APIが別のパターンを返した場合（比率変更など）は、更新済みのキャッシュで次のPVから新しいパターンを表示し、
          // そのときにCookieも更新される。これにより「実際に見たパターン」と「CVを紐付けるパターン」が常に一致する
          this.saveExperimentToCookie(
            appliedCache.caseId,
            appliedCache.pattern,
            appliedCache.experimentId,
          );
        } else {
          // キャッシュはこのURL（クエリ込み）専用なので、同じURLで実験が返らない・別の実験が返るのは、
          // キャッシュの実験が終了したとき。以降のゴールPVが終了済みの実験に紐付かないよう Cookie を削除する
          this.clearExperimentCookie(appliedCache.experimentId);
        }
      } else if (
        responseData.caseId &&
        responseData.pattern &&
        responseData.experimentId
      ) {
        // 割り当て（コントロール群を含む）をこのページとCookieに保存
        this.assignExperiment({
          caseId: responseData.caseId,
          pattern: responseData.pattern,
          experimentId: responseData.experimentId,
        });
        if (responseData.xPath && responseData.customHtml) {
          this.applyVariant({
            caseId: responseData.caseId,
            pattern: responseData.pattern,
            xPath: responseData.xPath,
            customHtml: responseData.customHtml,
          });
        }
      }

      // 実験なし・コントロール群・適用失敗の場合もここで表示する
      this.removeAntiFlickerStyle();

      // PVログは実験の有無に関わらず毎回送信する。
      // ゴールページはAPIが caseId: null を返すが、Cookieから復元した caseId/pattern で送ることで
      // ターゲット→ゴールの遷移（CV）が集計される
      this.track(EVENT_NAMES.PV_EVENT);
    } catch (error) {
      console.error(`[AgileBase] Failed to fetch variant:`, error);
      // APIで確認できない場合、キャッシュで表示したパターンを割り当てとして保存する
      if (appliedCache) {
        this.saveExperimentToCookie(
          appliedCache.caseId,
          appliedCache.pattern,
          appliedCache.experimentId,
        );
      }
      // エラー時もPVログは送信（キャッシュ適用済みならそのパターン、なければCookieから復元した情報を使用）
      this.track(EVENT_NAMES.PV_EVENT);
      // アンチフリッカースタイルを削除
      this.removeAntiFlickerStyle();
    }
  }

  /**
   * /variant API を呼び出す。API が応答しない場合にページが表示されないままにならないよう、
   * 本文（JSON）の受信が終わるまでをタイムアウトの対象にする（超えた場合は reject し、呼び出し元の catch で元のページを表示する）
   */
  private async fetchVariant(request: {
    visitorId: string;
    url: string;
  }): Promise<VariantResponse> {
    const controller =
      typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutId = controller
      ? setTimeout(() => controller.abort(), VARIANT_TIMEOUT_MS)
      : null;
    try {
      const response = await fetch(`${API_BASE_URL}/variant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'agile-base-service-key': `${this.serviceKey}`,
        },
        body: JSON.stringify(request),
        ...(controller ? { signal: controller.signal } : {}),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      // ヘッダー受信後に本文で止まった場合も abort で reject されるよう、json() の完了までタイマーを解除しない
      return (await response.json()) as VariantResponse;
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  }

  /**
   * 実験の割り当てを、このページのイベントとCookie（ゴールページなど実験対象外のページでの紐付け用）に保存する
   */
  private assignExperiment(data: {
    caseId: string;
    pattern: string;
    experimentId: string;
  }): void {
    this.setCurrentExperiment(data);
    this.saveExperimentToCookie(data.caseId, data.pattern, data.experimentId);
  }

  /**
   * このページのイベントを紐付ける実験情報を設定（Cookieは変更しない）
   */
  private setCurrentExperiment(data: {
    caseId: string;
    pattern: string;
    experimentId: string;
  }): void {
    this.currentCaseId = data.caseId;
    this.currentPattern = data.pattern;
    this.currentExperimentId = data.experimentId;
  }

  /**
   * バリアントを表示に反映する。キャッシュ・APIレスポンスの両経路から呼ぶ唯一の入口で、
   * DOM適用の前後に必要な処理はここに集約する。適用に成功した場合のみアンチフリッカーを解除する
   * （失敗時はAPIの再検証結果を待ってから解除する）
   */
  private applyVariant(variant: {
    caseId: string;
    pattern: string;
    xPath: string;
    customHtml: string;
  }): boolean {
    const applied = this.applyCustomHtmlModification(variant);
    if (applied) {
      this.removeAntiFlickerStyle();
    }
    return applied;
  }

  /**
   * カスタムHTMLによるDOM修正を適用
   * xPathで指定された要素にカスタムHTMLを挿入
   */
  private applyCustomHtmlModification(data: {
    id?: string | undefined;
    caseId?: string | undefined;
    pattern?: string | undefined;
    xPath: string;
    customHtml: string;
  }): boolean {
    debugLog(`[AgileBase] Applying custom HTML modification`, data);

    try {
      // xPathで要素を取得
      const element = this.getElementByXPath(data.xPath);

      if (!element) {
        console.warn(`[AgileBase] Element not found for xPath: ${data.xPath}`);
        return false;
      }

      debugLog(`[AgileBase] Inserting custom HTML at xPath: ${data.xPath}`);

      // カスタムHTMLを挿入（要素を置き換え）
      // API レスポンス・キャッシュいずれの経路でも DOM 適用前にサニタイズする
      // insertAdjacentHTML('afterend') は親要素を文脈に解析するため、サニタイズも同じ文脈で行う
      const safeHtml = sanitizeHtml(
        data.customHtml,
        element.parentElement ?? undefined,
      );
      if (!safeHtml) {
        debugWarn(
          `[AgileBase] Sanitized HTML is empty, skipping DOM modification for xPath: ${data.xPath}`,
        );
        return false;
      }
      element.insertAdjacentHTML('afterend', safeHtml);
      element.remove();

      // 分析用にケースIDとパターンを記録。
      // キャッシュ経路ではこのページの割り当てを設定する前に呼ばれるため、紐付ける実験を明示的に渡す
      if (data.caseId && data.pattern) {
        this.track(
          'ab_test_applied',
          {
            caseId: data.caseId,
            pattern: data.pattern,
            xPath: data.xPath,
            id: data.id,
          },
          { caseId: data.caseId, pattern: data.pattern },
        );
      }
      return true;
    } catch (error) {
      console.error(
        `[AgileBase] Failed to apply custom HTML modification:`,
        error,
      );
      return false;
    }
  }

  /**
   * xPathを使用して要素を取得
   */
  private getElementByXPath(xpath: string): Element | null {
    try {
      const result = document.evaluate(
        xpath,
        document,
        null,
        XPathResult.FIRST_ORDERED_NODE_TYPE,
        null,
      );
      return result.singleNodeValue as Element;
    } catch (error) {
      console.error(`[AgileBase] Invalid xPath: ${xpath}`, error);
      return null;
    }
  }

  /**
   * アンチフリッカースタイルをDOMに注入する。
   * 埋め込みコードがインラインスタイル（id="itera-antiflicker"）を既に配置している場合は注入をスキップする。
   * どちらの場合も、一定時間後に強制的に解除するフォールバックを必ず登録する。
   */
  private injectAntiFlickerStyle(): void {
    // フォールバック: API が応答しない・エラーの場合でも、一定時間後に必ずページを表示する。
    // インラインスタイルが配置済みの場合も解除の責任はタグにあるため、注入の有無に関わらず登録する
    setTimeout(() => {
      this.removeAntiFlickerStyle();
    }, ANTI_FLICKER_TIMEOUT_MS);

    try {
      // 新旧どちらかのIDが既に存在する場合（インラインスタイル含む）は注入しない
      if (
        document.getElementById(ANTI_FLICKER_ID) ||
        document.getElementById(ANTI_FLICKER_ID_LEGACY)
      ) {
        return;
      }

      const style = document.createElement('style');
      style.id = ANTI_FLICKER_ID;
      style.textContent = `
        body {
          opacity: 0 !important;
          transition: none !important;
        }
      `;

      // headが存在しない場合はdocument.documentElementに追加
      const target = document.head || document.documentElement;
      target.insertBefore(style, target.firstChild);

      debugLog('[AgileBase] Anti-flicker style injected');
    } catch (error) {
      console.error('[AgileBase] Failed to inject anti-flicker style:', error);
    }
  }

  /**
   * アンチフリッカースタイルを削除
   * DOM変更完了後またはエラー時に呼び出して、非表示にしていた要素を表示する
   */
  private removeAntiFlickerStyle(): void {
    try {
      // 新旧両IDを削除（旧 agile-base-antiflicker からの移行ユーザー互換対応）
      document.getElementById(ANTI_FLICKER_ID)?.remove();
      document.getElementById(ANTI_FLICKER_ID_LEGACY)?.remove();
      debugLog('[AgileBase] Anti-flicker style removed');
    } catch (error) {
      console.error('[AgileBase] Failed to remove anti-flicker style:', error);
    }
  }

  /**
   * XPath Pickerの初期化
   */
  private initXPathPicker(): void {
    // 既に初期化済みかチェック
    if ((window as any).ABTestToolXPathPicker) {
      return;
    }

    debugLog('[AgileBase] Initializing XPath Picker');
    (window as any).ABTestToolXPathPicker = new XPathPickerSnippet();
  }
}

/**
 * XPath Pickerクラス
 * 開発者がCase作成時にXPathを取得するためのツール
 */
class XPathPickerSnippet {
  private isActive: boolean = false;
  private highlightedElement: HTMLElement | null = null;
  private parentOrigin: string | null = null;
  private originalStyles: Map<
    HTMLElement,
    { outline: string; backgroundColor: string; zIndex: string }
  > = new Map();

  constructor() {
    this.init();
  }

  private init(): void {
    // メッセージリスナーを設定
    window.addEventListener('message', this.handleMessage.bind(this));

    // 親ウィンドウに準備完了を通知
    this.notifyReady();
  }

  private handleMessage(event: MessageEvent): void {
    // セキュリティチェック（許可されたオリジンのみ）
    if (
      !event.origin.includes('localhost') &&
      !event.origin.includes('127.0.0.1') &&
      !event.origin.includes('stg.itera.adorbia.com') &&
      !event.origin.includes('itera.adorbia.com')
    ) {
      return;
    }

    this.parentOrigin = event.origin;

    const { type } = event.data;

    switch (type) {
      case 'START_XPATH_PICKER':
        this.startPicker();
        break;
      case 'STOP_XPATH_PICKER':
        this.stopPicker();
        break;
      case 'PING':
        this.sendMessage('PONG', {});
        break;
    }
  }

  public startPicker(): void {
    if (this.isActive) return;

    this.isActive = true;
    document.body.style.cursor = 'crosshair';

    // イベントリスナーを追加
    document.addEventListener('mouseover', this.handleMouseOver.bind(this));
    document.addEventListener('mouseout', this.handleMouseOut.bind(this));
    document.addEventListener('click', this.handleClick.bind(this));

    // オーバーレイスタイルを追加
    this.addOverlayStyles();

    // ユーザーガイド表示
    this.showGuide();

    this.sendMessage('PICKER_STARTED', {});
  }

  public stopPicker(): void {
    if (!this.isActive) return;

    this.isActive = false;
    document.body.style.cursor = '';

    // イベントリスナーを削除
    document.removeEventListener('mouseover', this.handleMouseOver);
    document.removeEventListener('mouseout', this.handleMouseOut);
    document.removeEventListener('click', this.handleClick);

    // ハイライトを削除
    this.clearHighlight();

    // オーバーレイスタイルを削除
    this.removeOverlayStyles();

    // ガイドを削除
    this.hideGuide();

    this.sendMessage('PICKER_STOPPED', {});
  }

  private handleMouseOver(event: MouseEvent): void {
    if (!this.isActive) return;

    event.stopPropagation();
    this.highlightElement(event.target as HTMLElement);
  }

  private handleMouseOut(event: MouseEvent): void {
    if (!this.isActive) return;

    event.stopPropagation();
    this.clearHighlight();
  }

  private handleClick(event: MouseEvent): void {
    if (!this.isActive) return;

    event.preventDefault();
    event.stopPropagation();

    const target = event.target as HTMLElement;
    const xpath = this.generateXPath(target);
    const elementInfo = {
      xpath: xpath,
      tagName: target.tagName.toLowerCase(),
      id: target.id || '',
      className: target.className || '',
      textContent: target.textContent?.trim().substring(0, 100) || '',
      attributes: this.getElementAttributes(target),
    };

    this.sendMessage('XPATH_SELECTED', elementInfo);
    this.stopPicker();
  }

  private highlightElement(element: HTMLElement): void {
    this.clearHighlight();
    this.highlightedElement = element;

    // 元のスタイルを保存
    this.originalStyles.set(element, {
      outline: element.style.outline,
      backgroundColor: element.style.backgroundColor,
      zIndex: element.style.zIndex,
    });

    // ハイライトスタイルを適用
    element.style.outline = '2px solid #ff6b35';
    element.style.backgroundColor = 'rgba(255, 107, 53, 0.1)';
    element.style.zIndex = '999999';
  }

  private clearHighlight(): void {
    if (this.highlightedElement) {
      const original = this.originalStyles.get(this.highlightedElement);
      if (original) {
        this.highlightedElement.style.outline = original.outline;
        this.highlightedElement.style.backgroundColor =
          original.backgroundColor;
        this.highlightedElement.style.zIndex = original.zIndex;
        this.originalStyles.delete(this.highlightedElement);
      }
      this.highlightedElement = null;
    }
  }

  private generateXPath(element: HTMLElement): string {
    if (element.id) {
      return `//*[@id="${element.id}"]`;
    }

    let path = '';
    let current: HTMLElement | null = element;

    while (current && current.nodeType === Node.ELEMENT_NODE) {
      let index = 1;
      let sibling = current.previousElementSibling;

      while (sibling) {
        if (sibling.tagName === current.tagName) {
          index++;
        }
        sibling = sibling.previousElementSibling;
      }

      const tagName = current.tagName.toLowerCase();
      const segment = index > 1 ? `${tagName}[${index}]` : tagName;
      path = `/${segment}${path}`;

      current = current.parentElement;
    }

    return path.startsWith('/') ? path : `/${path}`;
  }

  private getElementAttributes(element: HTMLElement): Record<string, string> {
    const attributes: Record<string, string> = {};
    for (let i = 0; i < element.attributes.length; i++) {
      const attr = element.attributes[i];
      if (attr) {
        attributes[attr.name] = attr.value;
      }
    }
    return attributes;
  }

  private addOverlayStyles(): void {
    if (document.getElementById('abtest-xpath-picker-styles')) return;

    const style = document.createElement('style');
    style.id = 'abtest-xpath-picker-styles';
    style.textContent = `
      .abtest-xpath-picker-guide {
        position: fixed !important;
        top: 10px !important;
        right: 10px !important;
        background: rgba(0, 0, 0, 0.9) !important;
        color: white !important;
        padding: 12px 16px !important;
        border-radius: 6px !important;
        font-size: 14px !important;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
        z-index: 1000000 !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
        max-width: 300px !important;
      }

      .abtest-xpath-picker-guide-title {
        font-weight: bold !important;
        margin-bottom: 8px !important;
        color: #ff6b35 !important;
      }

      .abtest-xpath-picker-guide-text {
        font-size: 12px !important;
        line-height: 1.4 !important;
        margin-bottom: 8px !important;
      }

      .abtest-xpath-picker-guide-button {
        background: #ff6b35 !important;
        color: white !important;
        border: none !important;
        padding: 6px 12px !important;
        border-radius: 4px !important;
        cursor: pointer !important;
        font-size: 12px !important;
      }

      .abtest-xpath-picker-guide-button:hover {
        background: #e55a2b !important;
      }
    `;
    document.head.appendChild(style);
  }

  private removeOverlayStyles(): void {
    const style = document.getElementById('abtest-xpath-picker-styles');
    if (style) {
      style.remove();
    }
  }

  private showGuide(): void {
    if (document.getElementById('abtest-xpath-picker-guide')) return;

    const guide = document.createElement('div');
    guide.id = 'abtest-xpath-picker-guide';
    guide.className = 'abtest-xpath-picker-guide';
    guide.innerHTML = `
      <div class="abtest-xpath-picker-guide-title">XPath取得モード</div>
      <div class="abtest-xpath-picker-guide-text">
        取得したい要素の上にカーソルを合わせ、クリックしてください。
      </div>
      <button class="abtest-xpath-picker-guide-button" onclick="window.ABTestToolXPathPicker.stopPicker()">
        終了
      </button>
    `;
    document.body.appendChild(guide);
  }

  private hideGuide(): void {
    const guide = document.getElementById('abtest-xpath-picker-guide');
    if (guide) {
      guide.remove();
    }
  }

  private sendMessage(type: string, data: any): void {
    if (this.parentOrigin) {
      window.parent.postMessage({ type, data }, this.parentOrigin);
    }
  }

  private notifyReady(): void {
    // 少し遅延してから通知（iframeの読み込み完了を待つ）
    setTimeout(() => {
      window.parent.postMessage({ type: 'SNIPPET_READY' }, '*');
    }, 1000);
  }
}

if (typeof window !== 'undefined') {
  (window as unknown as { analytics: Analytics }).analytics = new Analytics();
}
